export interface AmbassadorInitiative {
  id: string;
  title: string;
  type: string;
  toolName?: string;
  summary: string;
  plannedDeliveryDate: string;
  isPublic: boolean;
  isActive: boolean;
  isToolNameRequired?: boolean;
  ambassadorId: string;
  ambassadorFirstName?: string;
  ambassadorLastName?: string;
  updatedAt?: string;
}

export interface AmbassadorInitiativesGroup {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  projectCount: number;
  projects: AmbassadorInitiative[];
}

export interface AmbassadorInitiativesApiItem {
  id?: string;
  _id?: string;
  title?: string;
  type?: string;
  toolName?: string;
  summary?: string;
  plannedDeliveryDate?: string;
  isPublic?: boolean;
  isActive?: boolean;
  isToolNameRequired?: boolean;
  ambassadorId?: string;
  firstName?: string;
  lastName?: string;
  ambassador?: {
    id?: string;
    firstName?: string;
    lastName?: string;
  };
  updatedAt?: string;
}

export interface AmbassadorInitiativesGroupApiItem {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  projectCount?: number;
  projects?: AmbassadorInitiativesApiItem[];
}

function normalizeAmbassadorInitiative(
  item: AmbassadorInitiativesApiItem,
  ambassador?: {
    id?: string;
    firstName?: string;
    lastName?: string;
  }
): AmbassadorInitiative {
  return {
    id: item.id || item._id || "",
    title: item.title ?? "",
    type: item.type ?? "",
    toolName: item.toolName,
    summary: item.summary ?? "",
    plannedDeliveryDate: item.plannedDeliveryDate ?? "",
    isPublic: Boolean(item.isPublic),
    isActive: Boolean(item.isActive),
    isToolNameRequired: item.isToolNameRequired,
    ambassadorId:
      item.ambassadorId || item.ambassador?.id || ambassador?.id || "",
    ambassadorFirstName:
      item.firstName ??
      item.ambassador?.firstName ??
      ambassador?.firstName ??
      "",
    ambassadorLastName:
      item.lastName ?? item.ambassador?.lastName ?? ambassador?.lastName ?? "",
    updatedAt: item.updatedAt,
  };
}

function normalizeAmbassadorInitiatives(
  data: unknown
): AmbassadorInitiative[] {
  if (!Array.isArray(data)) return [];
  return data.map((item) =>
    normalizeAmbassadorInitiative(item as AmbassadorInitiativesApiItem)
  );
}

function normalizeInitiativesGroupedByAmbassador(
  data: unknown
): AmbassadorInitiativesGroup[] {
  if (!Array.isArray(data)) return [];

  return data.map((item) => {
    const group = item as AmbassadorInitiativesGroupApiItem;
    const ambassador = {
      id: group.id || "",
      firstName: group.firstName || "",
      lastName: group.lastName || "",
    };
    const projects = Array.isArray(group.projects)
      ? group.projects.map((project) =>
          normalizeAmbassadorInitiative(project, ambassador)
        )
      : [];

    return {
      id: ambassador.id,
      firstName: ambassador.firstName,
      lastName: ambassador.lastName,
      email: group.email || "",
      company: group.company || "",
      projectCount:
        typeof group.projectCount === "number"
          ? group.projectCount
          : projects.length,
      projects,
    };
  });
}

function flattenGroupedInitiatives(
  groups: AmbassadorInitiativesGroup[] = []
): AmbassadorInitiative[] {
  return groups.flatMap((group) => group.projects || []);
}

export {
  normalizeAmbassadorInitiatives,
  normalizeInitiativesGroupedByAmbassador,
  flattenGroupedInitiatives,
};
